import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import SEO, { SEO_CONFIG } from '../components/SEO';
import CourseRequestForm from '../features/courses/components/CourseRequestForm';
import { useCreateCourseStream } from '../features/courses/hooks/useCreateCourseStream';
import { firstNameFromUser, pickLearningVerb } from '../utils/greeting';

// Maps the hook's streaming phase onto CourseRequestForm's status prop.
// 'idle' is overridden locally (see `dismissed`) so Cancel/Try-Different/
// Dismiss can return to the form without waiting on a fresh create() call.
function mapPhaseToStatus(phase) {
  if (phase === 'creating') return 'loading';
  if (phase === 'done') return 'success';
  return phase; // 'idle' | 'content_warning' | 'error' | 'connection_lost'
}

function buildErrorMessage(error) {
  if (!error) return '';
  if (typeof error === 'string') return error;
  if (error.code === 'RATE_LIMIT_EXCEEDED') {
    const secs = error?.data?.retryAfterSeconds;
    if (secs) {
      const mins = Math.max(1, Math.ceil(secs / 60));
      return `Too many requests. Please try again in ${mins} minute${mins === 1 ? '' : 's'}.`;
    }
    return error.message || 'Too many requests. Please try again later.';
  }
  return error.message || 'Failed to create course. Please try again.';
}

export default function CourseRequestPage() {
  const navigate = useNavigate();
  const { enabled, user } = useAuth();
  const { create, cancel, phase, stages, error, result } = useCreateCourseStream();

  const firstName = firstNameFromUser(user);
  // Pick a verb once per mount so it stays stable while the user is on
  // the page but varies across visits.
  const verb = useMemo(() => pickLearningVerb(), []);
  const heroTitle = firstName
    ? `What are we ${verb}, ${firstName}?`
    : `What are we ${verb} today?`;

  // Locally masks a stale terminal phase (error/content_warning) back to
  // 'idle' after the user dismisses it, without needing the hook to expose
  // a reset — the next create() call overwrites phase anyway.
  const [dismissed, setDismissed] = useState(false);
  const lastPayloadRef = useRef(null);
  const navigatedRef = useRef(false);

  const handleSubmit = async (formValues) => {
    if (phase === 'creating' || phase === 'done') return;
    if (!formValues.title) return;

    const payload = {
      title: formValues.title,
      advancedSettings: formValues.advancedSettings,
    };

    lastPayloadRef.current = payload;
    navigatedRef.current = false;
    setDismissed(false);

    await create({ payload, authEnabled: enabled });
  };

  // Navigate into the freshly-created course once the stream settles.
  // Falls back to /dashboard if a response ever lands without a courseId
  // so success never strands the user on the modal.
  useEffect(() => {
    if (phase === 'done' && !navigatedRef.current) {
      navigatedRef.current = true;
      const destination = result?.courseId ? `/courses/${result.courseId}` : '/dashboard';
      const t = setTimeout(() => navigate(destination), 1500);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [phase, result, navigate]);

  const handleRetry = () => {
    if (lastPayloadRef.current) {
      handleSubmit(lastPayloadRef.current);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
  };

  const status = dismissed ? 'idle' : mapPhaseToStatus(phase);
  const errorMessage = status === 'content_warning'
    ? 'This topic may violate content guidelines. Please try a different topic.'
    : buildErrorMessage(error);

  return (
    <>
    <SEO {...SEO_CONFIG.courseRequest} />
    <CourseRequestForm
      onSubmit={handleSubmit}
      status={status}
      errorMessage={errorMessage}
      onRetry={handleRetry}
      onDismiss={handleDismiss}
      stages={stages}
      onCancel={cancel}
      successSubtitle="Taking you to your new course..."
      heroTitle={heroTitle}
    />
    </>
  );
}
